@use '@/styles/tokens' as *;

/* Figma 594:527173 — a 21px circle carrying its number, CENTRED on the marked coordinate.
 *
 * <MapOverlay> puts its box's top-left at the projected pixel, so the translate is what makes the
 * Point sit on the coordinate rather than down-right of it — which is the whole of "plotted at
 * exactly the coordinates clicked".
 *
 * 21px and the 2px ring are the exported ellipse's own numbers (circle r=9.5, stroke-width 2 inside
 * a 21×21 viewBox); both sit off the --space-* scale and are written raw exactly as
 * device-markers.module.scss does for its own 40/56px pin. `box-sizing: border-box` keeps the ring
 * inside the 21px, as the SVG's r=9.5 does.
 *
 * Ticket 06 splits the colourway: `.point` below carries the geometry plus the DEFAULT (white,
 * every Point but the black one) colours; `.recent` overrides to black for the single black
 * Point. Which one applies is derived from `selectedIndex`/array position in marked-points.tsx,
 * never stored. */

/* Half the marker's own 21px height — the same "keep the CIRCLE centred on the coordinate whatever
 * else is in the column" correction `device-markers.module.scss`'s `.stack` makes at its own
 * marker's half-height. Moved here from `.point` so the tooltip can render above it in normal flow
 * without pulling the circle off its coordinate. */
.stack {
  position: absolute;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: $space-1;
  transform: translate(-50%, calc(-100% + 10.5px));
}

.point {
  @include reset-button;
  @include focus-ring;

  /* An in-flow item of `.stack` above, not positioned itself — see the comment there. */
  box-sizing: border-box;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 21px;
  height: 21px;
  border: 2px solid $color-neutral-950;
  border-radius: 50%;
  background: var(--surface-card);
  font-size: $font-size-10;
  font-weight: $font-weight-semibold;
  line-height: 1;
  color: $color-neutral-950;
  /* <MapOverlay>'s container takes no pointer events and an interactive child opts back in. */
  pointer-events: auto;
  /* `grab`, not the canvas's `crosshair`: over a Point the gesture on offer is moving it, not
   * marking another one. */
  cursor: grab;
}

/* The black Point — the one whose tooltip is open, or else the most recently marked (Figma leaf
 * `594:533328` — `fill="#111111" stroke="#FBFCFC"`, both exact token matches; number bound to
 * `Text/Inverse`). Which Point wears it is derived in marked-points.tsx, never stored. */
.recent {
  border-color: $color-white-100;
  background: $color-neutral-950;
  color: $color-white-100;
}

/* The Point tooltip — UNDESIGNED in Figma (grilling Q11; the whole of `587:523594` carries no
 * tooltip node). Every value comes from the dark appearance of this repo's own `<Tooltip>`
 * (`common/tooltip/tooltip.module.scss`), so the two dark tooltips in this app are the same
 * tooltip; the layout is admin's `map-marker-tooltip`. No fixed width — a Point number reaches two
 * digits, so this sizes to its content rather than clipping one. */
.tooltip {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: $space-1;
  padding: $space-1 $space-2;
  border-radius: $radius-xl;
  background: $color-neutral-950;
  box-shadow: var(--shadow-overlay);
  pointer-events: auto;
  white-space: nowrap;
  cursor: default;
}

.tooltipHeader {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: $space-2;
  width: 100%;
  font-size: $font-size-xs;
  font-weight: $font-weight-regular;
  color: $color-neutral-50;
}

.deleteButton {
  @include reset-button;
  @include focus-ring;

  display: flex;
}

/* De-emphasised against the label above them. */
.coords {
  display: flex;
  flex-direction: column;
  font-size: $font-size-10;
  font-weight: $font-weight-regular;
  color: $color-white-700;
}

/* The 10px square, rotated 45°, that points from the tooltip down at the circle. Raw, like the
 * 21px circle above it: nothing on the radius or space scales is 10px. */
.arrow {
  width: 10px;
  height: 10px;
  margin-top: -5px;
  border-radius: $radius-sm;
  background: $color-neutral-950;
  transform: rotate(45deg);
}
